import { any, func, bool, object, arrayOf, shape, string } from "prop-types";
import React, { useState, useEffect, Fragment } from "react";
import { connect } from "react-redux";

import ReadingStatus from "../ReadingListDownload/reading_status";
import {
  markDocumentReadPusterla,
  updateUserInProfileSilent,
} from "../../redux/actions";

import {
  getPusterlaReadingPDF, // <-- for the declaration reading list PDFs` : '' }
  getPusterlaPDF,
} from "../../api/profileApi";
import OnboardingArea from "../../common/OnboardingSectionCreator";
import {
  triggerDownload,
  filenameFromUrl,
} from "../../common/OnboardingSectionCreator/helpers";
import { forceDownload } from "../../containers/ResourceFolders/util";
import Convictions from "../../containers/Convictions";
import { Button } from "../../common";
import CriminalConvictionComp from "./CriminalConviction";

const useExtraInfoState = (initialState) => {
  const [state, setState] = useState(initialState);
  const handleChange = (key, val) => {
    setState({
      ...state,
      [key]: val,
    });
  };
  return [state, handleChange];
};

const PusterlaOnboarding = ({
  appData,
  readingList,
  markDocumentRead,
  pusterlaReadingLoading,
  updateProfile,
}) => {
  const { pusterlaOnboarding } = { ...{ pusterlaOnboarding: {} }, ...appData };

  const [criminalConviction, setCriminalConviction] = useState(
    appData && appData.criminalConviction
  );
  const [criminalConvictionDetails, setCriminalConvictionDetails] = useState(
    appData && appData.criminalConvictionDetails
  );

  const [loading, setLoading] = useState(false);
  const [curTypeCode, setCurTypeCode] = useState("");

  const getReadingPDF = () => {
    getPusterlaReadingPDF().then((response) => {
      forceDownload(response, "Reading List File");
    });
  };

  const submitted = false;

  useEffect(() => {
    setLoading(false);
  }, [submitted]);

  const readDocuments =
    (pusterlaOnboarding && pusterlaOnboarding.readDocuments) || [];

  const noCheckBoxConsentFiles = {
    // files for which no download will be shown after candidate has agreed
    "Welcome Booklet": true,
    "Raising Concerns and Grievances": true,
  };

  const retryCounter =
    (appData &&
      appData.pusterlaOnboarding &&
      appData.pusterlaOnboarding.retryCounter) ||
    0;

  const score =
    (appData &&
      appData.pusterlaOnboarding &&
      appData.pusterlaOnboarding.score) ||
    0;

  const agreementTextValues = {
    // custom agreement text
    // object here e.g. modernSlavery: 'I confirm ...'
    "Modern Slavery": `I confirm I have read and understood the Modern Slavery document and agree to what has been stated.`,
    "Company Induction": `I confirm I have read and understood the Company Induction and agree to what has been stated.`,
    "Manual Handling": `I confirm I have read and understood the Manual Handling document and agree to what has been stated.`,
    "Safety Knives": `I confirm I have read and understood the Safety Knives document and agree to what has been stated.`,
    "Anti Bribery and Corruption Overview": `I confirm I have read and understood the Anti Bribery and Corruption Overview document and agree to what has been stated.`,
    "Ethics Overview": `I confirm I have read and understood the Ethics Overview document and agree to what has been stated.`,
    "Human Rights Overview": `I confirm I have read and understood the Human Rights Overview document and agree to what has been stated.`,
    "Glue Lines System of Work": `I confirm I have read and understood the Glue Lines System of Work and agree to what has been stated.`,
  };

  const onFlyDocs = ["Glue Lines System of Work"];

  const [currentlyReading, setCurrentlyReading] = useState(null);

  const readingListWithStatus = readingList.map((download) => {
    return {
      ...download,
      noCheckBoxConsent: noCheckBoxConsentFiles[download.title] || false,
      agreementText: agreementTextValues[download.title] || "",
      status: readDocuments.includes(filenameFromUrl(download.link))
        ? ReadingStatus.Done
        : ReadingStatus.Todo,
      onFly: onFlyDocs.includes(download.title),
    };
  });

  const leftColumnComponents = [
    // TODO allow blocks do this as <Array <Array>> so each index is a block with the below object [13/05/22]
    {
      title: "Reading List",
      subtext:
        "You are required to download and read each document before you start work.  For some documents you will be asked to to tick the box to confirm you have read and understood and agree to it.  If you have any queries, please speak to your line manager.",
      formsListWithStatus: [],
      readingListWithStatus,
      currentlyReading,
      setCurrentlyReading,
      markDocumentRead,
      isLoading: pusterlaReadingLoading,
    },
  ];

  const retryCount = 3 - retryCounter;
  const formsListWithStatus = [
    {
      status:
        pusterlaOnboarding.YPUP && score === 5
          ? ReadingStatus.Done
          : ReadingStatus.Todo,
      submitted: score === 5 && pusterlaOnboarding.YPUP,
      loading: loading && curTypeCode === "YPUP",
      title: "Pusterla Pollards Questionnaire",
      url: `/profile/edit/pusterla-onboarding/assessment`,
      disabled: retryCount <= 0 && score !== 5,
      text: retryCount === 3 ? "Complete" : "Retake",
      retry: score !== 5,
      handleClick: pusterlaOnboarding.YPUP
        ? () =>
            triggerDownload(
              "YPUP",
              "Induction Questionnaire.pdf",
              setCurTypeCode,
              setLoading
            )
        : null,
    },
  ];

  const changeItem = (key, value) => {
    if (key === "criminalConviction") setCriminalConviction(value);
    else if (key === "criminalConvictionDetails")
      setCriminalConvictionDetails(value);
  };

  const validateCriminalConviction = () => {
    if (criminalConviction) return criminalConvictionDetails;
    return true;
  };
  const disableSubmitButton = () => {
    return !validateCriminalConviction();
  };

  const handleSubmit = () => {
    const { candidateRef, emailAddress } = appData;
    const updatedUserData = {
      emailAddress,
      /**
       * `homeTelephone`, `address`, and `addressRef` are here because we are using
       * the `Note` field in the profile API. They need to be sent with the payload
       * to ensure address fields aren't wiped
       */
      homeTelephone: appData.homeTelephon,
      address: appData.address,
      addressRef: appData.Ref,
      criminalConviction,
      criminalConvictionDetails: criminalConviction
        ? criminalConvictionDetails
        : null,
    };

    return Promise.all([updateProfile(candidateRef, updatedUserData)]);
  };

  const rightColumnComponents = [
    {
      title: `Questionnaire`,
      subtext:
        score === 5
          ? "Congratulations you have passed the Pusterla Questionnaire. If you have any queries about the questionnaire, please speak to your line manager."
          : [
              `Please complete the questionnaire below. If you have any queries about the questionnaire, please speak to your line manager.`,
              `This test has a limit of 3 attempts, you have ${
                retryCount <= 0 ? "0" : retryCount
              } attempts remaining.`,
            ],
      formsListWithStatus,
    },
    // {
    //   type: "component",
    //   component: () => CriminalConvictionComp(criminalConviction, changeItem, criminalConvictionDetails, handleSubmit, disableSubmitButton),
    // },
  ];

  return (
    <Fragment>
      <OnboardingArea
        title="Pusterla Pollards Training Pack"
        leftColumnComponents={leftColumnComponents}
        rightColumnComponents={rightColumnComponents}
        leftSubmit={() => {}}
        hasDBS={false}
        markDocumentRead={markDocumentRead}
        getReadingPDF={getReadingPDF}
        isLoading={pusterlaReadingLoading}
        criminalConviction={CriminalConvictionComp(
          criminalConviction,
          changeItem,
          criminalConvictionDetails,
          handleSubmit,
          disableSubmitButton
        )}
      />
    </Fragment>
  );
};

PusterlaOnboarding.propTypes = {
  appData: any,
  readingList: arrayOf(
    shape({
      title: string.isRequired,
      link: string.isRequired,
    })
  ),
  markDocumentRead: func.isRequired,
  pusterlaReadingLoading: bool,
  pursterlaOnboarding: object,
  updateProfile: func,
};

const mapStateToProps = (state) => ({
  appData: state.core.app,
  pusterlaOnboarding: state.core.app.pusterlaOnboarding,
  readingList: state.core.umbracoContent.pusterlaReadingList,
  pusterlaReadingLoading: state.core.pusterlaReading.isLoading,
});

const mapDispatchToProps = {
  markDocumentRead: markDocumentReadPusterla,
  updateProfile: updateUserInProfileSilent,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(PusterlaOnboarding);
